import { Button, Text, VSpacer } from '@/components/DesignSystem';
import { downloadCsv } from '@/utilities/Export';
import Download from '@mui/icons-material/Download';
import { QueryKeys } from '@/constants/QueryKeys';
import { useGetRetailerList } from '@/hooks/useProductQuery';
import { ManufacturerApi } from '@/utilities/api/ManufacturerApi';
import { MetricsApi } from '@/utilities/api/MetricsApi';
import { RewardsProgramApi } from '@/utilities/api/RewardsProgramApi';
import { MetricsEndpoint } from '@api/endpoints';
import { RewardsProgramType } from '@shared/enums';
import { Alert, Autocomplete, Container, Skeleton, Stack, TextField } from '@mui/material';
import { formatLocalDate } from '@shared/utilities';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useSearchParams } from 'react-router-dom';
import { programTypeLabel } from '@/pages/Admin/PointsAudit/programTypeLabel';
import { periodLabel } from './periodLabel';
import { buildBreakdownCsv, buildFundingCsv } from './metricsCsv';
import { BreakdownTable, FundingTable } from './PointsMetricsTables';

const outstandingPeriodId = 'outstanding';

type PeriodOption = {
  endDate?: string,
  id: string,
  /** A placeholder for a selected retailer that has no program periods. */
  isEmptyRetailer?: boolean,
  programType?: RewardsProgramType,
  retailerId?: string,
  startDate?: string,
};

const exportCsv = (csv: string, name: string) => {
  downloadCsv(csv, `points-metrics-${name}-${formatLocalDate(new Date())}`, {
    withByteOrderMark: true,
  });
};

const SectionHeader = ({ onExport, testID, title }: {
  onExport: () => void,
  testID: string,
  title: string,
}) => {
  const { t } = useTranslation();
  return (
    <Stack alignItems="center" direction="row" justifyContent="space-between">
      <Text category="title-medium">{title}</Text>
      <Button onClick={onExport} startIcon={<Download />} testID={testID} variant="outlined">
        {t($ => $.common.actions.export)}
      </Button>
    </Stack>
  );
};

/** Point totals across retailers, manufacturers and program periods, for testing and debugging. */
export const PointsMetrics = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const retailerIds = searchParams.getAll('retailerId');
  const programIds = searchParams.getAll('programId');
  const manufacturerIds = searchParams.getAll('manufacturerId');
  const includeOutstanding = searchParams.get('outstanding') !== 'false';

  const { retailerList } = useGetRetailerList();
  const retailers = retailerList ?? [];
  const sortedRetailerIds = [...retailerIds].sort();
  const {
    data: allPrograms = [],
    error: programsError,
    isPreviousData: areProgramsStale,
    isSuccess: areProgramsFetched,
  } = useQuery(
    [QueryKeys.LIST_REWARDS_PROGRAMS, 'metrics', sortedRetailerIds],
    () => RewardsProgramApi.list({
      minimal: true,
      retailerIds: sortedRetailerIds,
      type: Object.values(RewardsProgramType),
    }),
    { enabled: retailerIds.length > 0, keepPreviousData: true },
  );
  const areProgramsLoaded = areProgramsFetched && !areProgramsStale;
  const { data: allManufacturers = [] } = useQuery(
    [QueryKeys.GET_MANUFACTURERS, 'metrics', sortedRetailerIds],
    () => ManufacturerApi.list({ retailerIds: sortedRetailerIds }),
    { enabled: retailerIds.length > 0, keepPreviousData: true },
  );

  // A program from a retailer that isn't selected, say from an edited or shared link, has no chip
  // to remove it by. Once the selection's programs have loaded it is ignored, and the next filter
  // change drops it from the URL.
  const selectedProgramIds = areProgramsLoaded
    ? programIds.filter((id) => allPrograms.some((program) => program.id === id))
    : programIds;

  const retailerNames = new Map(retailers.map((retailer) => [retailer.id, retailer.name]));
  const selectedRetailers = retailers.filter((retailer) => retailerIds.includes(retailer.id));
  // Grouped Autocomplete options must arrive sorted by their group.
  const compareByRetailerName = (a: { retailerId?: string }, b: { retailerId?: string }) => (
    (retailerNames.get(a.retailerId ?? '') ?? '')
      .localeCompare(retailerNames.get(b.retailerId ?? '') ?? '')
  );

  const outstandingOption: PeriodOption = { id: outstandingPeriodId };
  const programOptions: PeriodOption[] = allPrograms
    .filter((program) => retailerIds.includes(program.retailerId))
    .map((program) => ({ ...program, programType: program.type }));
  const emptyRetailerOptions: PeriodOption[] = (areProgramsLoaded ? retailerIds : [])
    .filter((retailerId) => !programOptions.some((option) => option.retailerId === retailerId))
    .map((retailerId) => ({ id: `empty:${retailerId}`, isEmptyRetailer: true, retailerId }));
  const periodOptions = [
    outstandingOption,
    ...[...programOptions, ...emptyRetailerOptions].sort((a, b) => (
      compareByRetailerName(a, b)
      || (b.startDate ?? '').localeCompare(a.startDate ?? '')
      || (a.programType ?? '').localeCompare(b.programType ?? '')
    )),
  ];
  const selectedPeriods = periodOptions.filter((option) => (
    option.id === outstandingPeriodId ? includeOutstanding : programIds.includes(option.id)
  ));

  const manufacturerOptions = allManufacturers
    .filter((manufacturer) => retailerIds.includes(manufacturer.retailerId))
    .sort((a, b) => compareByRetailerName(a, b) || (a.name ?? '').localeCompare(b.name ?? ''));
  const selectedManufacturers = manufacturerOptions.filter((m) => manufacturerIds.includes(m.id));

  const updateFilters = (filters: {
    includeOutstanding?: boolean,
    manufacturerIds?: string[],
    programIds?: string[],
    retailerIds?: string[],
  }) => {
    const next = new URLSearchParams();
    const nextRetailerIds = filters.retailerIds ?? retailerIds;
    // Drops only ids known to belong to a deselected retailer; ids missing from the lists (still
    // loading, or an energy program from a shared link) are kept for the API to validate.
    const isForDeselectedRetailer = (
      id: string,
      options: { id: string, retailerId: string }[],
    ) => {
      const option = options.find((candidate) => candidate.id === id);
      return !!option && !nextRetailerIds.includes(option.retailerId);
    };
    nextRetailerIds.forEach((id) => next.append('retailerId', id));
    (filters.programIds ?? selectedProgramIds)
      .filter((id) => !isForDeselectedRetailer(id, allPrograms))
      .forEach((id) => next.append('programId', id));
    (filters.manufacturerIds ?? manufacturerIds)
      .filter((id) => !isForDeselectedRetailer(id, allManufacturers))
      .forEach((id) => next.append('manufacturerId', id));
    if (!(filters.includeOutstanding ?? includeOutstanding)) {
      next.set('outstanding', 'false');
    }
    setSearchParams(next, { replace: true });
  };

  const request: MetricsEndpoint.GetPointsMetrics.Request = {
    includeOutstanding,
    manufacturerId: manufacturerIds,
    programId: selectedProgramIds,
    retailerId: retailerIds,
  };
  const canQuery = retailerIds.length > 0
    && (includeOutstanding || selectedProgramIds.length > 0)
    && (!programIds.length || areProgramsLoaded);
  const { data: metrics, error, isFetching } = useQuery(
    [QueryKeys.GET_POINTS_METRICS, request],
    () => MetricsApi.getPointsMetrics(request),
    { enabled: canQuery, keepPreviousData: true },
  );
  const outstandingTotals = metrics?.totals.outstanding;
  const programPeriodTotals = metrics?.totals.programPeriods;

  const getPeriodLabel = (option: PeriodOption) => {
    if (option.isEmptyRetailer) {
      return t($ => $.pointsMetrics.filters.noProgramPeriods);
    }
    return periodLabel(option.id === outstandingPeriodId ? null : option, t);
  };

  const getSelectedPeriodLabel = (option: PeriodOption) => (
    option.retailerId && !option.isEmptyRetailer && retailerIds.length > 1
      ? t($ => $.pointsMetrics.retailerPeriodLabel, {
        endDate: option.endDate,
        programType: programTypeLabel(option.programType ?? RewardsProgramType.Agronomy, t),
        retailerName: retailerNames.get(option.retailerId),
        startDate: option.startDate,
      })
      : getPeriodLabel(option)
  );

  return (
    <Container maxWidth="xl">
      <VSpacer size="7" />
      <Text category="headline-medium">{t($ => $.pointsMetrics.title)}</Text>
      <VSpacer size="3" />
      <Text category="body-medium">{t($ => $.pointsMetrics.description)}</Text>
      <VSpacer size="6" />

      <Stack direction="row" flexWrap="wrap" gap="16px">
        <Autocomplete
          getOptionLabel={(retailer) => retailer.name}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          multiple
          onChange={(_, value) => updateFilters({ retailerIds: value.map((r) => r.id) })}
          options={retailers}
          renderInput={(params) => (
            <TextField
              {...params}
              data-testid="points-metrics-retailer-select"
              label={t($ => $.pointsMetrics.filters.retailers)}
            />
          )}
          sx={{ minWidth: 320 }}
          value={selectedRetailers}
        />

        <Autocomplete
          disableCloseOnSelect
          getOptionDisabled={(option) => !!option.isEmptyRetailer}
          getOptionLabel={getSelectedPeriodLabel}
          groupBy={(option) => (
            option.retailerId ? retailerNames.get(option.retailerId) ?? '' : ''
          )}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          multiple
          onChange={(_, value) => updateFilters({
            includeOutstanding: value.some((option) => option.id === outstandingPeriodId),
            programIds: value
              .filter((option) => option.id !== outstandingPeriodId)
              .map((option) => option.id),
          })}
          options={periodOptions}
          renderInput={(params) => (
            <TextField
              {...params}
              data-testid="points-metrics-period-select"
              label={t($ => $.pointsMetrics.filters.periods)}
            />
          )}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>{getPeriodLabel(option)}</li>
          )}
          sx={{ minWidth: 360 }}
          value={selectedPeriods}
        />

        <Autocomplete
          disableCloseOnSelect
          getOptionLabel={(manufacturer) => manufacturer.name ?? manufacturer.id}
          groupBy={(manufacturer) => retailerNames.get(manufacturer.retailerId) ?? ''}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          multiple
          onChange={(_, value) => updateFilters({ manufacturerIds: value.map((m) => m.id) })}
          options={manufacturerOptions}
          renderInput={(params) => (
            <TextField
              {...params}
              data-testid="points-metrics-manufacturer-select"
              label={t($ => $.pointsMetrics.filters.manufacturers)}
              placeholder={selectedManufacturers.length
                ? undefined
                : t($ => $.pointsMetrics.filters.allManufacturers)}
            />
          )}
          sx={{ minWidth: 320 }}
          value={selectedManufacturers}
        />
      </Stack>

      <VSpacer size="6" />

      {!!programsError && (
        <Alert severity="error">{(programsError as Error).message}</Alert>
      )}
      {!canQuery && !programsError && (
        <Text category="body-medium">{t($ => $.pointsMetrics.empty)}</Text>
      )}
      {canQuery && !!error && (
        <Alert severity="error">{(error as Error).message}</Alert>
      )}
      {canQuery && !metrics && !error && (
        <Skeleton height="240px" variant="rectangular" />
      )}
      {canQuery && !!metrics && !error && (
        <Stack gap="24px" sx={{ opacity: isFetching ? 0.6 : 1 }}>
          {!!outstandingTotals && (
            <Stack gap="8px">
              <SectionHeader
                onExport={() => exportCsv(
                  buildFundingCsv(outstandingTotals, t),
                  'outstanding-totals',
                )}
                testID="points-metrics-outstanding-totals-export"
                title={t($ => $.pointsMetrics.sections.outstanding)}
              />
              <FundingTable
                funding={outstandingTotals}
                testID="points-metrics-outstanding-totals"
              />
            </Stack>
          )}
          {!!programPeriodTotals && (
            <Stack gap="8px">
              <SectionHeader
                onExport={() => exportCsv(
                  buildFundingCsv(programPeriodTotals, t),
                  'program-period-totals',
                )}
                testID="points-metrics-program-period-totals-export"
                title={t($ => $.pointsMetrics.sections.programPeriods)}
              />
              <FundingTable
                funding={programPeriodTotals}
                testID="points-metrics-program-period-totals"
              />
            </Stack>
          )}
          <Stack gap="8px">
            <SectionHeader
              onExport={() => exportCsv(buildBreakdownCsv(metrics.rows, t), 'breakdown')}
              testID="points-metrics-breakdown-export"
              title={t($ => $.pointsMetrics.sections.breakdown)}
            />
            <BreakdownTable rows={metrics.rows} />
          </Stack>
          <Stack component="ul" gap="4px" sx={{ m: 0, pl: 3 }}>
            {[
              t($ => $.pointsMetrics.notes.outstanding),
              t($ => $.pointsMetrics.notes.available),
              t($ => $.pointsMetrics.notes.combined),
              t($ => $.pointsMetrics.notes.promotions),
            ].map((note) => (
              <li key={note}>
                <Text category="body-small">{note}</Text>
              </li>
            ))}
          </Stack>
        </Stack>
      )}
      <VSpacer size="7" />
    </Container>
  );
};
